import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateKPIForm } from "../hooks/useUpdateKPIForm";
import { divisions } from "@/shared/data/division";

export const UpdateKPIForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    form,
    title,
    setTitle,
    description,
    setDescription,
    division,
    setDivision,
    selectedQuestionLists,
    availableQuestionLists,
    isLoading,
    isLoadingLists,
    isSubmitting,
    handleQuestionListToggle,
    moveQuestionListUp,
    moveQuestionListDown,
    handleSubmit,
  } = useUpdateKPIForm(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Card className="w-full max-w-3xl">
          <CardContent className="pt-6">
            <div className="text-center">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  const isDraft = form.status === "draft";

  return (
    <div className="flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <CardTitle>Update KPI Form</CardTitle>
          <CardDescription>
            Update form details, add/remove/reorder question lists
            {!isDraft && (
              <span className="text-yellow-600 block mt-2">
                ⚠️ Warning: This form is {form.status}. You cannot modify
                question lists.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Form Title</Label>
              <Input
                id="title"
                placeholder="e.g., Q1 2024 Performance Review"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                placeholder="Brief description of this form"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="division">Division</Label>
              <Select value={division} onValueChange={setDivision}>
                <SelectTrigger>
                  <SelectValue placeholder="Select division" />
                </SelectTrigger>
                <SelectContent>
                  {divisions.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isDraft && (
              <div className="space-y-2">
                <Label>Select Question Lists</Label>
                <div className="border rounded-md p-4 space-y-2 max-h-60 overflow-y-auto">
                  {isLoadingLists ? (
                    <p className="text-sm text-muted-foreground">
                      Loading question lists...
                    </p>
                  ) : availableQuestionLists.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No question lists available
                    </p>
                  ) : (
                    availableQuestionLists.map((list) => {
                      const isSelected = selectedQuestionLists.some(
                        (sql) => sql.questionListId === list._id
                      );
                      return (
                        <div
                          key={list._id}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                            isSelected ? "bg-primary/10" : "bg-muted"
                          }`}
                          onClick={() => handleQuestionListToggle(list._id)}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleQuestionListToggle(list._id)}
                            className="cursor-pointer"
                          />
                          <div className="flex-1">
                            <div className="font-medium">{list.title}</div>
                            <div className="text-sm text-muted-foreground">
                              {list.questions?.length || 0} questions
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {selectedQuestionLists.length > 0 && (
              <div className="space-y-2">
                <Label>Question List Order</Label>
                <div className="border rounded-md p-4 space-y-2">
                  {selectedQuestionLists.map((selectedList, index) => (
                    <div
                      key={selectedList.questionListId}
                      className="flex items-center gap-2 p-2 bg-muted rounded"
                    >
                      <span className="font-mono text-sm text-muted-foreground w-8">
                        {index + 1}.
                      </span>
                      <span className="flex-1">{selectedList.title}</span>
                      {isDraft && (
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => moveQuestionListUp(index)}
                            disabled={index === 0}
                          >
                            ↑
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => moveQuestionListDown(index)}
                            disabled={
                              index === selectedQuestionLists.length - 1
                            }
                          >
                            ↓
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {isDraft && (
                  <p className="text-sm text-muted-foreground">
                    Use ↑ ↓ buttons to reorder question lists
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/view-all-kpi-form")}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || (isDraft && selectedQuestionLists.length === 0)}
                className="flex-1"
                variant="default"
              >
                {isSubmitting ? "Updating..." : "Update Form"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
